import React, { useEffect, useState } from "react"
import { useConsultantDeclarationDetailsContext } from "../_ConsultantDeclarationDetails"
import { useQuery } from "react-query"
import { IConsultantNdfDetailModel, IPersonnelNdfDetailModel } from "@common-models/*"
import { AxiosError } from "axios"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"
import MyTooltip from "@common-utils/MyTooltip"
import { getDeclarationActiveNdfDetails } from "../../core/_requests"
import MyModal from "@common-utils/MyModal"
import MyLoadingOverlay from "../../../../../../../common/components/MyLoadingOverlay"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import Footer from "./_Footer"
import { CONST_CONSULTANT_CONTRAT_ST, CONST_NDF_DETAILS_CATEGORIE_KM } from "@common-constants/*"
import clsx from "clsx"

const REACT_QUERY_KEY_GET_CONSULTANT_NDF = "REACT_QUERY_KEY_GET_CONSULTANT_NDF"

/**
 * Sous-composant pour gérer l'affichage tronqué de la description
 */
const DescriptionCell = ({ description }: { description: string }) => {
     const [isExpanded, setIsExpanded] = useState(false)
     const charLimit = 150 // Limite de caractères avant de tronquer

     if (description.length <= charLimit) {
          return (
               <div className={"block"}>
                    <span className={"fw-bold"}>Description : </span> {description}
               </div>
          )
     }

     return (
          <div className={"block"}>
               <span className={"fw-bold"}>Description : </span>
               <span className="text-gray-800">{isExpanded ? description : `${description.substring(0, charLimit)}...`}</span>
               <button type="button" className="btn btn-link p-0 ms-2 fw-bolder text-primary fs-9 uppercase transition-all" onClick={() => setIsExpanded(!isExpanded)}>
                    {isExpanded ? "Voir moins" : "Voir plus"}
               </button>
          </div>
     )
}

const SmallCalendarWidget = ({ joursStr, dateString }: { joursStr?: string[] | number[]; dateString?: string }) => {
     if (!joursStr || joursStr.length === 0 || !dateString) return null

     const [year, month] = dateString.split("-").map(Number)
     const daysInMonth = new Date(year, month, 0).getDate()
     const joursKm = joursStr.map(Number)

     return (
          <div className="mt-4 p-3 bg-light-primary bg-opacity-25 border border-primary border-dashed rounded">
               <div className="d-flex flex-wrap gap-1">
                    {Array.from({ length: daysInMonth }, (_, i) => {
                         const d = i + 1
                         const dt = new Date(year, month - 1, d)
                         const isWE = dt.getDay() === 0 || dt.getDay() === 6
                         const letter = new Intl.DateTimeFormat("fr-FR", { weekday: "narrow" }).format(dt)
                         const isSel = joursKm.includes(d)

                         return (
                              <div
                                   key={d}
                                   className={clsx(
                                        "d-flex flex-column align-items-center justify-content-center rounded",
                                        isSel
                                             ? "bg-primary text-white shadow-sm"
                                             : isWE
                                             ? "bg-light border border-gray-300 opacity-50"
                                             : "bg-white border border-gray-300 text-gray-700"
                                   )}
                                   style={{ width: "26px", height: "30px", fontSize: "0.65rem" }}
                                   title={isSel ? "Trajet effectué" : isWE ? "Week-end" : "Jour de semaine"}
                              >
                                   <span className="fw-bolder" style={{ lineHeight: "1" }}>
                                        {letter}
                                   </span>
                                   <span className="fw-boldest" style={{ lineHeight: "1", marginTop: "2px" }}>
                                        {d}
                                   </span>
                              </div>
                         )
                    })}
               </div>
          </div>
     )
}

const ConsultantDeclarationDetailsNDF = () => {
     const context = useConsultantDeclarationDetailsContext()
     const declaration_id = context.declaration.id!

     const [totalDepenses, setTotalDepenses] = useState<number>(0)

     const [documentInModal, setDocumentInModal] = useState<{
          driveID: string
          modalTitle: string
          isLoading: boolean
     }>()

     const declarationActiveNdfDetailsQuery = useQuery<IConsultantNdfDetailModel[], AxiosError>(REACT_QUERY_KEY_GET_CONSULTANT_NDF, () =>
          getDeclarationActiveNdfDetails(declaration_id).then(r => r.data)
     )

     useEffect(() => {
          computeTotalExpenses(declarationActiveNdfDetailsQuery.data || [])
     }, [declarationActiveNdfDetailsQuery.data])

     function computeTotalExpenses(items: IPersonnelNdfDetailModel[]) {
          let total = 0
          items.map(item => {
               if (item.isParent && item.enfants) {
                    item.enfants.map(enfant => {
                         total += enfant.montantTtc as number
                    })
               } else {
                    total += item.montantTtc as number
               }
          })
          setTotalDepenses(total)
     }

     // Is fetching
     if (declarationActiveNdfDetailsQuery.isFetching) {
          return (
               <div className={"text-center p-5"}>
                    <MySimpleSpinner size={"xl"} />{" "}
               </div>
          )
     }

     // Error
     if (!declarationActiveNdfDetailsQuery.isFetching && declarationActiveNdfDetailsQuery.isError) {
          return (
               <MyAlert type={"danger"} classNames={"mb-4"}>
                    <>
                         <span className={"me-2"}>{declarationActiveNdfDetailsQuery.error?.response?.data?.detail}</span>
                         <button className={"btn btn-sm btn-danger"} onClick={() => declarationActiveNdfDetailsQuery.refetch()}>
                              Recharger
                         </button>
                    </>
               </MyAlert>
          )
     }

     // Empty data
     if (
          !declarationActiveNdfDetailsQuery.isFetching &&
          !declarationActiveNdfDetailsQuery.isError &&
          declarationActiveNdfDetailsQuery.data &&
          declarationActiveNdfDetailsQuery.data.length === 0
     ) {
          return (
               <div className={"text-center p-4"}>
                    <p style={{ fontWeight: 500 }} className={"mb-4"}>
                         Aucune justificatif n'a été ajouté ...
                    </p>
                    <Footer />
               </div>
          )
     }

     const Salarie = () => {
          return (
               <div className={"row"}>
                    {declarationActiveNdfDetailsQuery.data?.map((item, key) => (
                         <div className="card shadow-sm col-sm-6 col-lg-4 mb-4 p-0 pe-sm-2" key={key}>
                              <div className="card-header bg-info border border-info" style={{ minHeight: "2rem" }}>
                                   <h3 className="card-title text-white align-items-center">
                                        <span className={"fas fa-file-invoice me-2"}></span>
                                        {item.isParent ? `Justificatifs groupés ( ${item.enfants?.length} )` : item.categorieLabel}
                                   </h3>
                              </div>
                              <div className="card-body border-info border">
                                   <div className="d-flex justify-content-center">
                                        {item.consultantNdfDocuments?.map((item_, key_) => (
                                             <MyTooltip title={"Voir la pièce jointe"} key={key_}>
                                                  <div
                                                       className={`mb-4 d-flex justify-content-center fas fa-file-invoice cursor-pointer ${key_ > 0 ? "ms-4" : ""}`}
                                                       style={{ fontSize: "2rem" }}
                                                       onClick={() =>
                                                            setDocumentInModal({
                                                                 isLoading: true,
                                                                 modalTitle: "Justificatif",
                                                                 driveID: item_.googleDriveID as string,
                                                            })
                                                       }
                                                  />
                                             </MyTooltip>
                                        ))}
                                   </div>

                                   {/* Justificatif simple */}
                                   {!item.isParent && (
                                        <div className={"mb-4"}>
                                             <div className={"block"}>
                                                  <span className={"fw-bold"}>Date : </span> {item.date?.parseZone().format("DD/MM/YYYY")}
                                             </div>
                                             {item.categorie === CONST_NDF_DETAILS_CATEGORIE_KM && item.indemniteKmDetails && (
                                                  <>
                                                       <div className={"block"}>
                                                            <span className={"fw-bold"}>Départ : </span>{" "}
                                                            <a
                                                                 href={`https://www.google.com/maps/search/?api=1&query=${item.indemniteKmDetails.addresseDepart}`}
                                                                 target={"_blank"}
                                                                 rel={"noopener noreferrer"}
                                                            >
                                                                 {item.indemniteKmDetails.addresseDepart}
                                                            </a>
                                                       </div>
                                                       <div className={"block"}>
                                                            <span className={"fw-bold"}>Destination : </span>{" "}
                                                            <a href={`https://www.google.com/maps/search/?api=1&query=${item.indemniteKmDetails.addresseArrive}`} target={"_blank"}>
                                                                 {item.indemniteKmDetails.addresseArrive}
                                                            </a>
                                                       </div>
                                                       <div className={"block"}>
                                                            <span className={"fw-bold"}>Nb trajets : </span>{" "}
                                                            {item.indemniteKmDetails.isAllerRetour ? item.indemniteKmDetails.nbTrajets! * 2 : item.indemniteKmDetails.nbTrajets}
                                                       </div>
                                                       <div className={"block"}>
                                                            <span className={"fw-bold"}>Distance totale : </span> {item.indemniteKmDetails.distanceKmTotal?.toFixed(2)} Km
                                                       </div>
                                                       <SmallCalendarWidget joursStr={item.indemniteKmDetails.joursKm} dateString={item.date?.parseZone().format("YYYY-MM")} />
                                                  </>
                                             )}
                                             {/* Utilisation de DescriptionCell ici */}
                                             {item.description && <DescriptionCell description={item.description} />}
                                        </div>
                                   )}

                                   {/* Justificatif groupée */}
                                   {item.isParent && (
                                        <>
                                             {item.enfants && item.enfants.length > 0 && (
                                                  <>
                                                       {item.enfants.map((child, childIdx) => (
                                                            <div className={"mb-4"} key={childIdx}>
                                                                 <div className={"block fs-5 border-bottom border-dark mb-1 fw-bold align-items-center"}>
                                                                      Ticket n°{child.numeroTicket}
                                                                 </div>
                                                                 <div className={"block"}>
                                                                      <span className={"fw-bold"}>Date : </span> {child.date!.parseZone().format("dddd DD MMMM YYYY")}
                                                                 </div>
                                                                 <div className={"block"}>
                                                                      <span className={"fw-bold"}>Catégorie : </span> {child.categorieLabel}
                                                                 </div>
                                                                 <div className={"block"}>
                                                                      <span className={"fw-bold"}>Montant TTC : </span> {child.montantTtc?.toFixed(2)} €
                                                                 </div>
                                                                 {child.categorie === CONST_NDF_DETAILS_CATEGORIE_KM && child.indemniteKmDetails && (
                                                                      <>
                                                                           <div className={"block"}>
                                                                                <span className={"fw-bold"}>Départ : </span>{" "}
                                                                                <a
                                                                                     href={`https://www.google.com/maps/search/?api=1&query=${child.indemniteKmDetails.addresseDepart}`}
                                                                                     target={"_blank"}
                                                                                     rel={"noopener noreferrer"}
                                                                                >
                                                                                     {child.indemniteKmDetails.addresseDepart}
                                                                                </a>
                                                                           </div>
                                                                           <div className={"block"}>
                                                                                <span className={"fw-bold"}>Destination : </span>{" "}
                                                                                <a
                                                                                     href={`https://www.google.com/maps/search/?api=1&query=${child.indemniteKmDetails.addresseArrive}`}
                                                                                     target={"_blank"}
                                                                                >
                                                                                     {child.indemniteKmDetails.addresseArrive}
                                                                                </a>
                                                                           </div>
                                                                           <div className={"block"}>
                                                                                <span className={"fw-bold"}>Nb trajets : </span>{" "}
                                                                                {child.indemniteKmDetails.isAllerRetour
                                                                                     ? child.indemniteKmDetails.nbTrajets! * 2
                                                                                     : child.indemniteKmDetails.nbTrajets}
                                                                           </div>
                                                                           <div className={"block"}>
                                                                                <span className={"fw-bold"}>Distance totale : </span>{" "}
                                                                                {child.indemniteKmDetails.distanceKmTotal?.toFixed(2)} Km
                                                                           </div>
                                                                           <SmallCalendarWidget
                                                                                joursStr={child.indemniteKmDetails.joursKm}
                                                                                dateString={child.date?.format("YYYY-MM")}
                                                                           />
                                                                      </>
                                                                 )}
                                                                 {/* Utilisation de DescriptionCell pour les tickets groupés */}
                                                                 {child.description && <DescriptionCell description={child.description} />}
                                                            </div>
                                                       ))}
                                                  </>
                                             )}
                                        </>
                                   )}

                                   <div className="row border-top-dashed border-info pt-3">
                                        <div className="col-6 p-0"></div>
                                        <div className="col-6 fw-bold fs-5 d-flex align-items-center p-0 justify-content-end text-nowrap">
                                             Total :{" "}
                                             {item.isParent
                                                  ? item.enfants
                                                       ? Object.values(item.enfants.map(child => child.montantTtc as number))
                                                              .reduce((acc, val) => acc + val, 0)
                                                              .toFixed(2)
                                                       : 0
                                                  : item.montantTtc?.toFixed(2)}{" "}
                                             €
                                        </div>
                                   </div>
                              </div>
                         </div>
                    ))}
                    {declarationActiveNdfDetailsQuery?.data && declarationActiveNdfDetailsQuery?.data.length > 1 && (
                         <div className="d-flex justify-content-end px-2">
                              {/* round two decimals totaldepenses*/}
                              <div className={"alert alert-info px-2 py-1 text-dark fw-bold m-0"}>TOTAL: {totalDepenses.toFixed(2)} €</div>
                         </div>
                    )}
               </div>
          )
     }

     const SousTraitant = () => {
          const data = declarationActiveNdfDetailsQuery.data || []

          return (
               <div className="mb-10">
                    {data.map((ndf, ndfIndex) => {
                         const documents = ndf.consultantNdfDocuments || []
                         const total = ndf.montantTtc?.toFixed(2) || "0.00"

                         // Vérification de l'existence des montants pour l'affichage conditionnel [cite: 2]
                         const hasHt = ndf.montantHt !== null && ndf.montantHt !== undefined
                         const hasTva = ndf.montantTva !== null && ndf.montantTva !== undefined

                         return (
                              <div key={ndf.id || ndfIndex} className="bg-body border border-secondary border-opacity-25 rounded px-5 py-4 mb-4 shadow-xs transition-all">
                                   {/* En-tête de la dépense : Libellé, Détails montants et Montant Global */}
                                   <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 border-bottom border-secondary border-opacity-10 pb-3">
                                        <div className="d-flex align-items-center mb-3 mb-md-0 flex-grow-1">
                                             <div className="symbol symbol-40px bg-light-info border border-info border-opacity-10 me-5 d-flex align-items-center justify-content-center">
                                                  <i className="fas fa-file-invoice text-info fs-5"></i>
                                             </div>
                                             <div className="d-flex flex-column me-8">
                                                  <span className="text-gray-900 fw-bold fs-5">{ndf.categorieLabel || "Frais divers"}</span>
                                                  <span className="text-muted fs-7">{ndf.date ? ndf.date.format("DD/MM/YYYY") : ndf.createdAt?.format("DD/MM/YYYY")}</span>
                                             </div>

                                             {/* Détails HT et TVA (masqués sur mobile) - Conditionnés par l'existence des montants [cite: 2] */}
                                             {(hasHt || hasTva) && (
                                                  <div className="text-gray-600 fs-7 fw-semibold d-none d-lg-block">
                                                       {hasHt && <span className="badge badge-secondary me-2">HT: {ndf.montantHt?.toFixed(2)}€</span>}
                                                       {hasTva && <span className="badge badge-secondary">TVA: {ndf.montantTva?.toFixed(2)}€</span>}
                                                  </div>
                                             )}
                                        </div>

                                        {total && Number(total) > 0 && (
                                             <div className="d-flex align-items-center">
                                                  <span className="badge badge-light-dark fw-bolder fs-6 px-4 py-2">{total} €</span>
                                             </div>
                                        )}
                                   </div>

                                   {/* Liste des documents associés à cette dépense */}
                                   <div className="d-flex flex-column gap-2">
                                        {documents.map((doc, docIdx) => (
                                             <div key={doc.id || docIdx} className="d-flex align-items-center justify-content-between bg-light bg-opacity-50 rounded p-3">
                                                  <div className="d-flex align-items-center flex-grow-1 overflow-hidden me-4">
                                                       <i className="far fa-file-pdf text-danger me-3 fs-5"></i>
                                                       <span className="text-gray-700 fs-7 fw-semibold text-truncate">{doc.fileName || `Justificatif n°${docIdx + 1}`}</span>
                                                  </div>
                                                  <button
                                                       className="btn btn-sm btn-light-info fw-bold py-2"
                                                       onClick={() =>
                                                            setDocumentInModal({
                                                                 modalTitle: doc.fileName || `Justificatif`,
                                                                 driveID: doc.googleDriveID!,
                                                                 isLoading: true,
                                                            })
                                                       }
                                                  >
                                                       <i className="fas fa-eye me-2"></i>
                                                       Voir
                                                  </button>
                                             </div>
                                        ))}
                                        {documents.length === 0 && <span className="text-muted fs-8 italic ps-2">Aucune pièce jointe</span>}
                                   </div>
                              </div>
                         )
                    })}
               </div>
          )
     }

     return (
          <>
               {context.declaration.consultant?.contrat === CONST_CONSULTANT_CONTRAT_ST ? <SousTraitant /> : <Salarie />}

               <div className="separator separator-dashed my-5"></div>

               <Footer />

               {/* Document in modal */}
               {documentInModal && (
                    <MyModal title={<span>{documentInModal.modalTitle}</span>} show={true} onCloseCallback={() => setDocumentInModal(undefined)} noPadding size={"lg"}>
                         <MyLoadingOverlay isActive={documentInModal.isLoading}>
                              <iframe
                                   src={FileAndDriveHelper.getUrlOfDriveFilePreview(documentInModal.driveID)}
                                   onLoad={() => setDocumentInModal(prev => (prev ? { ...prev, isLoading: false } : prev))}
                                   style={{ width: "100%", minHeight: "82vh", overflowY: "scroll" }}
                              />
                         </MyLoadingOverlay>
                    </MyModal>
               )}
          </>
     )
}

export default ConsultantDeclarationDetailsNDF
